import React, { useState, useEffect, useCallback } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../Common/LoadingSpinner';
import PermissionGuard from '../../Common/PermissionGuard';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import ItemForm from './ItemForm';
import ItemBulkUpload from './ItemBulkUpload';
import inventoryApi from '../../../services/inventoryApi';

const ItemManagement = () => {
  // Items are fetched page-by-page directly via inventoryApi (like the Finance module's Banks/
  // JournalEntry pages and SupplierManagement.jsx), rather than the shared InventoryContext list.
  // categories/suppliers stay on the context "give me everything" pattern since they back dropdowns
  // (category filter, ItemForm's supplier picker) and are small lists.
  const {
    categories,
    fetchCategories,
    fetchSuppliers,
    deleteItem
  } = useInventory();

  const { hasPermission } = useAuth();

  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('itemManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Page-by-page server data (mirrors SupplierManagement.jsx / Finance's Banks.jsx)
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

  // Debounce search so every keystroke doesn't trigger a fetch
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);

      const params = { page: pagination.page, limit: pagination.limit };
      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();
      // isActive is the explicit true/false/all switch (see itemController.getItems) — always send it
      // so "All Status" doesn't silently fall back to the backend's isActive=true default.
      if (statusFilter === 'active') params.isActive = 'true';
      else if (statusFilter === 'inactive') params.isActive = 'false';
      else params.isActive = 'all';
      if (categoryFilter !== 'all') params.category = categoryFilter;

      const body = await inventoryApi.getItems(params);
      const list = Array.isArray(body.data) ? body.data : [];
      setItems(list);
      setPagination(prev => ({ ...prev, ...(body.pagination || {}) }));
      setFetchError(null);
    } catch (err) {
      console.error('Error loading items:', err);
      setFetchError(err.response?.data?.message || err.message || 'Failed to load items');
      setItems([]);
      setPagination(prev => ({ ...prev, total: 0, pages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearchTerm, statusFilter, categoryFilter]);

  // Reset to page 1 whenever a server-side filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    // Only fetch data if user has read permission
    if (hasPermission('INVENTORY:ITEMS:READ')) {
      loadItems();
    }
  }, [loadItems, hasPermission]);

  useEffect(() => {
    if (hasPermission('INVENTORY:CATEGORIES:READ')) {
      fetchCategories();
    }
    if (hasPermission('INVENTORY:SUPPLIERS:READ')) {
      fetchSuppliers(); // Fetch suppliers for the ItemForm
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- one-time mount fetch, mirrors prior behavior

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('itemManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryFilter = (e) => {
    setCategoryFilter(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleCreateItem = () => {
    if (!hasPermission('INVENTORY:ITEMS:CREATE')) {
      console.warn('Insufficient permissions to create items');
      return;
    }
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEditItem = (item) => {
    if (!hasPermission('INVENTORY:ITEMS:UPDATE')) {
      console.warn('Insufficient permissions to edit items');
      return;
    }
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDeleteItem = async (item) => {
    if (!hasPermission('INVENTORY:ITEMS:DELETE')) {
      console.warn('Insufficient permissions to delete items');
      return;
    }
    try {
      await deleteItem(item._id);
      setShowDeleteConfirm(null);
      await loadItems();
    } catch (err) {
      console.error('Failed to delete item:', err);
      setFetchError(err.response?.data?.message || err.message || 'Failed to delete item');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingItem(null);
    // Refresh items list if user has read permission
    if (hasPermission('INVENTORY:ITEMS:READ')) {
      loadItems();
    }
  };

  // search/category/status are already applied server-side (see loadItems above) — `items` is exactly
  // the current page's results.

  // Pagination handlers — trigger a re-fetch of that page via loadItems (see useEffect above)
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Windowed page-number buttons (1 ... current-1 current current+1 ... last), matching
  // client/src/components/Finance/Banks/Banks.jsx's getPaginationButtons()
  const getPaginationButtons = () => {
    const buttons = [];
    const totalPages = pagination.pages || 1;
    const currentPage = pagination.page || 1;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) buttons.push(i);
    } else {
      buttons.push(1);
      if (currentPage > 3) buttons.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) buttons.push(i);
      if (currentPage < totalPages - 2) buttons.push('...');
      if (totalPages > 1) buttons.push(totalPages);
    }
    return buttons;
  };

  const getItemIcon = (item) => {
    return item.sku.substring(0, 2).toUpperCase();
  };

  const getStockStatusBadge = (item) => {
    if (!item.currentStock || item.currentStock <= 0) {
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Out of Stock</span>;
    } else if (item.currentStock <= item.reorderPoint) {
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Low Stock</span>;
    } else {
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">In Stock</span>;
    }
  };

  const renderItemCard = (item) => {
    const categoryName = typeof item.category === 'object' ? item.category?.name : null;
    const sellPrice = item?.pricing?.sellingPrice;
    const stock = item.availableStock ?? item.currentStock ?? 0;
    const unit = item?.inventory?.unit || item.unitOfMeasure || '';

    return (
      <div key={item._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group w-full">
        <div className="p-3 flex flex-col gap-2">

          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                {getItemIcon(item)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 truncate">{item.name}</h3>
                <p className="text-xs text-gray-400">{item.sku}</p>
              </div>
            </div>
            <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              <PermissionGuard permission="INVENTORY:ITEMS:UPDATE">
                <button onClick={() => handleEditItem(item)} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all" title="Edit">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </button>
              </PermissionGuard>
              <PermissionGuard permission="INVENTORY:ITEMS:DELETE">
                <button onClick={() => setShowDeleteConfirm(item)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all" title="Delete">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </PermissionGuard>
            </div>
          </div>

          {/* Category + Price row */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="truncate">{categoryName || '—'}</span>
            <span className="font-medium text-gray-700 flex-shrink-0 ml-2">
              {sellPrice != null ? `Rs ${sellPrice.toFixed(2)}` : '—'}
            </span>
          </div>

          {/* Stock + Status */}
          <div className="flex items-center justify-between pt-1 border-t border-gray-100">
            <span className="text-xs text-gray-600">{stock} {unit}</span>
            {getStockStatusBadge(item)}
          </div>

        </div>
      </div>
    );
  };

  const renderItemRow = (item) => {
    const categoryName = typeof item.category === 'object' ? item.category?.name : null;
    const sellPrice = item?.pricing?.sellingPrice;
    const stock = item.availableStock ?? item.currentStock ?? 0;
    const unit = item?.inventory?.unit || item.unitOfMeasure || '';

    return (
      <tr key={item._id} className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
              {getItemIcon(item)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{item.name}</div>
              <div className="text-xs text-gray-400">{item.sku}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-sm text-gray-600">{categoryName || '—'}</span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <p className="text-sm text-gray-600 max-w-xs truncate">{item.description || '—'}</p>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          <span className="text-sm text-gray-900">{stock} {unit}</span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          {getStockStatusBadge(item)}
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-right">
          <span className="text-sm font-medium text-gray-900">{sellPrice != null ? `Rs ${sellPrice.toFixed(2)}` : '—'}</span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-right">
          <div className="flex items-center justify-end space-x-2">
            <PermissionGuard permission="INVENTORY:ITEMS:UPDATE">
              <button onClick={() => handleEditItem(item)} className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all" title="Edit">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
            </PermissionGuard>
            <PermissionGuard permission="INVENTORY:ITEMS:DELETE">
              <button onClick={() => setShowDeleteConfirm(item)} className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all" title="Delete">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </PermissionGuard>
          </div>
        </td>
      </tr>
    );
  };

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <PermissionGuard
      permission="INVENTORY:ITEMS:READ"
      showFallback={true}
      fallback={
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-gray-500 mb-2">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Access Restricted</h3>
            <p className="text-sm text-gray-500">
              You don't have permission to view items.
            </p>
          </div>
        </div>
      }
    >
      <div className="bg-white shadow-sm border border-gray-200">
          {/* Error Display */}
          {fetchError && (
            <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-800">{fetchError}</p>
                <button
                  onClick={() => setFetchError(null)}
                  className="ml-auto text-red-600 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Form Modal */}
          {showForm && (
            <ItemForm
              item={editingItem}
              onSuccess={handleFormSuccess}
              onCancel={() => {
                setShowForm(false);
                setEditingItem(null);
              }}
            />
          )}

          {/* Bulk upload modal */}
          {showBulkUpload && (
            <ItemBulkUpload
              onSuccess={() => {
                setShowBulkUpload(false);
                loadItems();
              }}
              onCancel={() => setShowBulkUpload(false)}
            />
          )}

          {/* Main Content */}
          {!showForm && (
            <>
              {/* Filters and Search */}
              <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                  {/* Search */}
                  <div className="flex-1">
                    <TextInput
                      variant="input"
                      type="text"
                      placeholder="Search items by name, SKU, or description..."
                      value={searchTerm}
                      onChange={handleSearch}
                      icon={({ className }) => (
                        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )}
                      className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                    />
                  </div>

                  {/* Filters and Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    {/* Category Filter */}
                    <CustomSelect
                      value={categoryFilter}
                      onChange={handleCategoryFilter}
                      options={[
                        { value: 'all', label: 'All Categories' },
                        ...categories.map(cat => ({ value: cat._id, label: cat.name }))
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[150px]"
                    />

                    {/* Status Filter */}
                    <CustomSelect
                      value={statusFilter}
                      onChange={handleStatusFilter}
                      options={[
                        { value: 'all', label: 'All Status' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' }
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[120px]"
                    />

                    {/* View Toggle */}
                    <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                      <button
                        onClick={() => handleViewChange('card')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        title="Card view"
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                        </svg>
                        Cards
                      </button>
                      <button
                        onClick={() => handleViewChange('list')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        title="List view"
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        List
                      </button>
                    </div>

                    {/* Add Item Button */}
                    <PermissionGuard permission="INVENTORY:ITEMS:CREATE">
                      <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowBulkUpload(true)}
                        className="inline-flex items-center px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-sm font-medium rounded-md shadow-sm hover:bg-indigo-50 transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Bulk upload
                      </button>
                      <button
                        onClick={handleCreateItem}
                        className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Item
                      </button>
                      </div>
                    </PermissionGuard>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="flex items-center justify-between px-2 py-2 bg-gradient-to-r from-gray-100 to-gray-100 border-b border-gray-200">
                    {/* Results Info */}
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-900 flex items-center gap-2">
                          <span>
                            {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} items
                          </span>
                          {loading && (
                            <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' ? 'Filtered results' : 'All items'}
                        </p>
                      </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center gap-3">
                      {/* Items per page */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 font-medium">Show:</span>
                        <CustomSelect
                          value={pagination.limit}
                          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                          disabled={loading}
                          options={[
                            { value: 10, label: '10' },
                            { value: 20, label: '20' },
                            { value: 50, label: '50' },
                            { value: 100, label: '100' }
                          ]}
                          size="sm"
                          variant="minimal"
                          className="min-w-[70px]"
                        />
                      </div>

                      {/* Navigation Buttons */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={pagination.page <= 1 || loading}
                          className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          title="First page"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                          </svg>
                        </button>

                        <button
                          onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                          disabled={pagination.page <= 1 || loading}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          <span className="hidden sm:inline">Previous</span>
                        </button>

                        {/* Page Numbers */}
                        <div className="hidden md:flex items-center gap-1">
                          {getPaginationButtons().map((button, index) => (
                            <button
                              key={index}
                              onClick={() => typeof button === 'number' && handlePageChange(button)}
                              disabled={button === '...' || loading}
                              className={`min-w-[28px] px-2 py-1.5 rounded text-xs font-medium transition-all ${button === pagination.page
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : button === '...'
                                ? 'text-gray-400 cursor-default'
                                : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                                }`}
                            >
                              {button}
                            </button>
                          ))}
                        </div>

                        {/* Mobile page indicator */}
                        <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                          <span className="text-xs font-medium text-gray-900">{pagination.page}</span>
                          <span className="text-xs text-gray-500 mx-1">of</span>
                          <span className="text-xs font-medium text-gray-900">{pagination.pages}</span>
                        </div>

                        <button
                          onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                          disabled={pagination.page >= pagination.pages || loading}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                        >
                          <span className="hidden sm:inline">Next</span>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        <button
                          onClick={() => handlePageChange(pagination.pages)}
                          disabled={pagination.page >= pagination.pages || loading}
                          className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          title="Last page"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
              )}
                  {/* Content */}
                  <div className="p-2">
                    {items.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No items found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' ?
                            'Try adjusting your search or filter criteria.' :
                            'Get started by creating your first item.'
                          }
                        </p>
                        {!searchTerm && categoryFilter === 'all' && statusFilter === 'all' && (
                          <div className="mt-6">
                            <PermissionGuard permission="INVENTORY:ITEMS:CREATE">
                              <button
                                onClick={handleCreateItem}
                                className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs font-medium rounded shadow-sm transition-all"
                              >
                                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Create Item
                              </button>
                            </PermissionGuard>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Card View */}
                        {view === 'card' && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {items.map(renderItemCard)}
                          </div>
                        )}

                        {/* List View */}
                        {view === 'list' && (
                          <div className="overflow-x-auto">
                            <table className="min-w-max w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Item</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Stock</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Price</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {items.map(renderItemRow)}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </div>
            </>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
              <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>

                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Item</h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.name}"</span>?
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      This action cannot be undone.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteItem(showDeleteConfirm)}
                      className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors"
                    >
                      Delete Item
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </PermissionGuard>
  );
};

export default ItemManagement;
